import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { isNaN, isArray, isEmpty, isEqual } from 'lodash';
import PropTypes from 'prop-types';
import qs from 'querystring';
import * as Yup from 'yup';

import {
  Spinner,
  Accordion,
  SelectDataInTable,
  Table,
  Modal,
} from '~/components';
import { Input, Autocomplete, Form, Select } from '~/components/Form';
import {
  OPERACAO_ABASTECIMENTO,
  LUBRIFICACAO,
  MODULO_AFERICAO_DVEC,
  CUSTOMIZACAO_RAIZEN,
  MODULO_FERROVIA,
} from '~/constants/modules';
import { t, validCharacters, can, number, canURL } from '~/helpers';
import api from '~/services/api';
import {
  storeModelosVeiculo,
  updateModelosVeiculo,
} from '~/store/modules/modeloveiculo/actions';

import TransferComponent from '../Transfer';
import * as S from './styles';

export default function CreateOrUpdate({
  reference,
  data,
  create,
  toggleLoading,
  toggleConfirmButton,
}) {
  const dispatch = useDispatch();
  const modalRef = useRef(null);
  const hiddenButtonPermission = isEqual(canURL(), { read: true });

  const [operations, setOperations] = useState([]);
  const [compartments, setCompartments] = useState([]);
  const [products, setProducts] = useState([]);
  const [disabled, setDisabled] = useState([]);
  const [tipoVeiculo, setTipoVeiculo] = useState('V'); // Veiculo
  const { client } = useSelector((state) => state.user.profile);
  const { loading, find, params } = useSelector((state) => state.modeloveiculo);
  const [pendingRequests, setPendingRequests] = useState(0);
  const [hasLoadingError, setHasLoadingError] = useState(false);
  const loadingErrorMessage = 's.geral.loadingerror';

  const initials = create ? data : { ...find, ...data };

  const handleAsyncLoadingChange = useCallback((isLoading) => {
    setPendingRequests((state) => {
      if (hasLoadingError) return 0;

      if (isLoading) return state + 1;

      return state > 0 ? state - 1 : 0;
    });
  }, [hasLoadingError]);

  const handleAsyncLoadingError = useCallback(() => {
    setHasLoadingError(true);
    setPendingRequests(0);
  }, []);

  const maxtag = useMemo(
    () => [
      { label: 'Não Definido', value: 0 },
      { label: '1', value: 1 },
      { label: '2', value: 2 },
      { label: '3', value: 3 },
      { label: '4', value: 4 },
      { label: '5', value: 5 },
      { label: '6', value: 6 },
      { label: '7', value: 7 },
      { label: '8', value: 8 },
      { label: '9', value: 9 },
      { label: '10', value: 10 },
    ],
    []
  );

  const cells = useMemo(
    () => [
      {
        id: 'codigonomeoperacao',
        label: t('s.cadastro.modeloveiculo.descricao'),
      },
      {
        id: 'consumomediomodeloveiculooperacao',
        label: t('s.cadastro.modeloveiculo.consumo'),
      },
    ],
    []
  );

  const totalizer = useMemo(
    () => [
      { value: 0, label: 'Não Possui' },
      { value: 1, label: t('s.operacao.abastecimento.horimetro') },
      { value: 2, label: t('s.operacao.abastecimento.hodometro') },
    ],
    []
  );

  const cellsCompartiments = useMemo(
    () => [
      {
        id: 'idcompartimento',
        label: t('s.cadastro.modeloveiculo.idcompartimento'),
      },
      {
        id: 'descricaocompartimento',
        label: t('s.cadastro.modeloveiculo.descricaocompartimento'),
      },
      {
        id: 'codigoauxcompartimento',
        label: t('s.cadastro.marcasveiculo.codigoauxiliarmarcaveiculo'),
      },
      {
        id: 'idproduto',
        label: t('s.menu.configuracao.produto'),
        // format: (value) => {
        //   if (isEmpty(products)) return '';
        //   const produto = products.find((val) => val.value === value);
        //   return produto ? produto.label : '';
        // },
        format: (value) =>
          isEmpty(products)
            ? ''
            : products.find((val) => val.value === value).label,
      },
      {
        id: 'capacidadecompartimento',
        label: t('s.cadastro.modeloveiculo.capacidadecompartimento'),
        format: (value) => number(value, 2),
        align: 'right',
      },
      // {
      //   id: 'tipototalizadorcompartimento',
      //   label: t('s.cadastro.veiculo.tipototalizadorveiculo'),
      //   format: (value) => totalizer.find((val) => val.value === value).label,
      // },
    ],
    [products]
  );

  const isFields = useMemo(
    () => [
      {
        id: 'idcompartimento',
        label: t('s.cadastro.modeloveiculo.idcompartimento'),
        type: 'number',
        disabledOnUpdate: true,
      },
      {
        id: 'codigoauxcompartimento',
        label: t('s.cadastro.marcasveiculo.codigoauxiliarmarcaveiculo'),
        type: 'text',
      },
      {
        id: 'descricaocompartimento',
        label: t('s.cadastro.modeloveiculo.descricaocompartimento'),
        type: 'text',
      },
      {
        id: 'idproduto',
        label: t('s.menu.configuracao.produto'),
        type: 'select',
        options: products,
      },
      {
        id: 'capacidadecompartimento',
        label: t('s.cadastro.modeloveiculo.capacidadecompartimento'),
        type: 'number',
      },
      {
        id: 'tipototalizadorcompartimento',
        label: t('s.cadastro.veiculo.tipototalizadorveiculo'),
        type: 'select',
        options: totalizer,
      },
    ],
    [products]
  );

  const schemaValidate = useMemo(
    () => ({
      idcompartimento: Yup.number()
        .required(t('s.geral.validacao.campoobrigatorio'))
        .min(1, t('s.geral.validacao.valorminimo').replace('%s', 1))
        .max(
          999999999,
          t('s.geral.validacao.valormaximo').replace('%s', 999999999)
        ),
      codigoauxcompartimento: Yup.string()
        .ensure()
        .trim()
        .nullable()
        .max(20, t('s.geral.validacao.tamanhomaximo').replace('%s', 20))
        .test(
          'characters-invalid',
          t('s.configuracao.geral.naopodecontercaracterespecial'),
          (value) => validCharacters(value)
        ),
      descricaocompartimento: Yup.string()
        .required(t('s.geral.validacao.campoobrigatorio'))
        .max(10, t('s.geral.validacao.valormaximo').replace('%s', 10)),
      idproduto: Yup.number().required(t('s.geral.validacao.campoobrigatorio')),
      capacidadecompartimento: Yup.number().required(
        t('s.geral.validacao.campoobrigatorio')
      ),
      tipototalizadorcompartimento: Yup.number().required(
        t('s.geral.validacao.campoobrigatorio')
      ),
    }),
    []
  );

  const accordions = useMemo(
    () => [
      {
        id: 'compartimento',
        title: t('s.cadastro.modeloveiculo.compartimento'),
        isVisible: can(LUBRIFICACAO),
        component: () => (
          <SelectDataInTable
            icons={false}
            checkbox={false}
            showPagination={false}
            paginate={99999}
            size="small"
            variant="contained"
            position="row"
            onChanged={(value) => setCompartments(value)}
            unique="idcompartimento"
            maxWidth="xs"
            initials={compartments}
            fields={isFields}
            schema={schemaValidate}
            cells={cellsCompartiments}
            titleModal={t('s.cadastro.modeloveiculo.compartimento')}
            disabled={can(CUSTOMIZACAO_RAIZEN)}
          />
        ),
      },
      {
        id: 'operacoes',
        title: t('s.cadastro.modeloveiculo.operacoes'),
        isVisible: can(OPERACAO_ABASTECIMENTO),
        component: () => (
          <Table
            showCount
            cells={cells}
            data={operations}
            height="40rem"
            size="small"
            unique="idoperacao"
            onCallback={
              hiddenButtonPermission
                ? (value) => value
                : () => modalRef.current.handleOpenModal()
            }
            // noButton={can(CUSTOMIZACAO_RAIZEN)}
            labelButton={t('s.geral.botaoalterar')}
          />
        ),
      },
    ],
    [cells, operations, products, hiddenButtonPermission]
  );

  const handleGetProducts = useCallback(async () => {
    try {
      handleAsyncLoadingChange(true);
      const response = await api.get('produtos', {
        params: {
          pagesize: 9999,
          wherefield: ['tipoproduto'],
          valuewherefield: ['C'],
          typewherefield: ['NEQUALS'],
          orderfield: 'nomeabreviadoproduto',
          ordination: 'asc',
          pesquisalista: true,
        },
        paramsSerializer: (param) => qs.stringify(param),
      });

      setProducts(
        response.data.items.map((product) => ({
          value: product.idproduto,
          label: product.nomeabreviadoproduto,
        }))
      );
    } catch (err) {
      handleAsyncLoadingError();
      if (isArray(err)) err.map((e) => toast.error(t(e)));
      else toast.error(t(err));
    } finally {
      handleAsyncLoadingChange(false);
    }
  }, [handleAsyncLoadingChange, handleAsyncLoadingError]);

  const handleSubmit = useCallback(
    async (param) => {
      try {
        if (hasLoadingError) {
          toast.error(t(loadingErrorMessage));
          return;
        }

        if (pendingRequests > 0) {
          toast.warning(t('s.geral.validacao.aguardecarregamento'));
          return;
        }

        const schema = Yup.object().shape({
          idmarcaveiculo: Yup.number(
            t('s.geral.validacao.informeumnumero')
          ).required(t('s.geral.validacao.campoobrigatorio')),
          nomemodeloveiculo: Yup.string()
            .trim()
            .required(t('s.geral.validacao.campoobrigatorio'))
            .min(1, t('s.geral.validacao.campoobrigatorio'))
            .max(50, t('s.geral.validacao.tamanhomaximo').replace('%s', 50))
            .test(
              'characters-invalid',
              t('s.configuracao.geral.naopodecontercaracterespecial'),
              (value) => validCharacters(value)
            ),
          codigoauxiliarmodeloveiculo: Yup.string()
            .ensure()
            .trim()
            .nullable()
            .max(20, t('s.geral.validacao.tamanhomaximo').replace('%s', 20))
            .test(
              'characters-invalid',
              t('s.configuracao.geral.naopodecontercaracterespecial'),
              (value) => validCharacters(value)
            ),
          codigofipemodeloveiculo: Yup.string()
            .ensure()
            .trim()
            .nullable()
            .max(20, t('s.geral.validacao.tamanhomaximo').replace('%s', 20))
            .test(
              'characters-invalid',
              t('s.configuracao.geral.naopodecontercaracterespecial'),
              (value) => validCharacters(value)
            ),
          consumomediomodeloveiculo:
            tipoVeiculo === 'V'
              ? Yup.number()
                  .required(t('s.geral.validacao.campoobrigatorio'))
                  .transform((value) => (isNaN(value) ? undefined : value))
                  .min(0, t('s.geral.validacao.valorminimo').replace('%s', 0))
                  .lessThan(
                    1000,
                    t('s.geral.validacao.valormaximo').replace('%s', 999)
                  )
              : Yup.number().nullable(),
          totalizadorentraafericaomodeloveiculo: can(MODULO_AFERICAO_DVEC)
            ? Yup.number()
                .required(t('s.geral.validacao.campoobrigatorio'))
                .transform((value) => (isNaN(value) ? undefined : value))
                .min(1, t('s.geral.validacao.valorminimo').replace('%s', 1))
                .lessThan(
                  100001,
                  t('s.geral.validacao.valormaximo').replace('%s', 100000)
                )
            : Yup.string().nullable(),
          maxtagveiculomodelo: Yup.number().required(
            t('s.geral.validacao.campoobrigatorio')
          ),
        });

        await schema.validate(param, {
          abortEarly: false,
        });

        const objSend = {
          ...initials,
          totalizadorentraafericaomodeloveiculo: 10000,
          ...param,
          operacoes: operations,
          compartimentos: compartments.map((comp) => ({
            ...comp,
            capacidadecompartimento: Number(comp.capacidadecompartimento),
            idmodeloveiculo: initials.idmodeloveiculo,
            idcliente: client,
          })),
          idcliente: client,
        };

        if (create) {
          await dispatch(storeModelosVeiculo(objSend, params));
        } else {
          await dispatch(updateModelosVeiculo(objSend, params));
        }
      } catch (err) {
        const validationErrors = {};

        if (err instanceof Yup.ValidationError) {
          err.inner.forEach((error) => {
            validationErrors[error.path] = error.message.replace(
              `${error.path} `,
              ''
            );
          });

          reference.current.setErrors(validationErrors);
        }
      }
    },
    [
      operations,
      compartments,
      tipoVeiculo,
      hasLoadingError,
      pendingRequests,
    ]
  );

  useEffect(() => {
    if (!loading && reference.current) {
      reference.current.setData(initials);
    }
  }, [loading, initials]);

  useEffect(() => {
    toggleLoading(pendingRequests > 0);
  }, [pendingRequests, toggleLoading]);

  useEffect(() => {
    toggleConfirmButton(!hasLoadingError);
  }, [hasLoadingError, toggleConfirmButton]);

  useEffect(
    () => () => {
      toggleLoading(false);
      toggleConfirmButton(true);
    },
    [toggleLoading, toggleConfirmButton]
  );

  useEffect(() => {
    if (can(LUBRIFICACAO)) {
      handleGetProducts();
    }
  }, []);

  useEffect(() => {
    if (!isEmpty(find) && !create) {
      if (find.operacoes) setOperations(find.operacoes);
      if (find.compartimentos) setCompartments(find.compartimentos);
      setTipoVeiculo(find.tipomodeloveiculo);
    } else if (create) {
      setTipoVeiculo('V');
    }
  }, [find, create]);

  return (
    <>
      {loading ? (
        <div style={{ height: '14rem', width: '50rem' }}>
          <Spinner loading />
        </div>
      ) : hasLoadingError ? (
        <div
          style={{
            border: '1px solid #f5c6cb',
            background: '#f8d7da',
            color: '#721c24',
            borderRadius: '4px',
            padding: '16px',
            lineHeight: 1.5,
          }}
        >
          {t(loadingErrorMessage)}
        </div>
      ) : (
        <div style={{ position: 'relative', minHeight: '14rem' }}>
          <Form ref={reference} initialData={initials} onSubmit={handleSubmit}>
            <Autocomplete
              name="idmarcaveiculo"
              variant="outlined"
              label="nomemarcaveiculo"
              size="small"
              url="marcasveiculos"
              title={t('s.cadastro.modeloveiculo.nomemarcaveiculo')}
              unique="idmarcaveiculo"
              fullWidth
              disabled={can(CUSTOMIZACAO_RAIZEN)}
              onLoadingChange={handleAsyncLoadingChange}
              onLoadingError={handleAsyncLoadingError}
            />

            {can(MODULO_FERROVIA) && (
              <Select
                name="tipomodeloveiculo"
                variant="outlined"
                label={t('s.cadastro.veiculo.tipoveiculo')}
                defaultValue={initials.tipomodeloveiculo || 'V'}
                disabled={!create}
                options={[
                  { value: 'V', label: t('s.menu.cadastro.veiculo') },
                  {
                    value: 'L',
                    label: t('s.menu.cadastro.veiculo.locomotiva'),
                  },
                ]}
                getValue={(value) => setTipoVeiculo(value)}
              />
            )}

            <Input
              name="nomemodeloveiculo"
              label={t('s.cadastro.modeloveiculo.nomemodeloveiculo')}
              disabled={can(CUSTOMIZACAO_RAIZEN)}
            />

            <Input
              name="codigoauxiliarmodeloveiculo"
              label={t('s.cadastro.modeloveiculo.codigoauxiliarmodeloveiculo')}
              disabled={can(CUSTOMIZACAO_RAIZEN)}
            />

            <Input
              name="codigofipemodeloveiculo"
              label={t('s.cadastro.modeloveiculo.codigofipemodeloveiculo')}
              disabled={can(CUSTOMIZACAO_RAIZEN)}
            />

            {tipoVeiculo == 'V' && (
              <Input
                name="consumomediomodeloveiculo"
                label={t('s.cadastro.modeloveiculo.consumomediomodeloveiculo')}
                type="number"
                disabled={can(CUSTOMIZACAO_RAIZEN)}
              />
            )}

            {can(MODULO_AFERICAO_DVEC) && (
              <Input
                name="totalizadorentraafericaomodeloveiculo"
                label={t('camposistema.totalizadorentraafericaomodeloveiculo')}
                type="number"
                disabled={can(CUSTOMIZACAO_RAIZEN)}
              />
            )}

            <Select
              name="maxtagveiculomodelo"
              label={t('s.cadastro.modeloveiculo.maxtagveiculomodelo')}
              options={maxtag}
            />
          </Form>

          <S.Wrapper>
            <Accordion accordions={accordions} />
          </S.Wrapper>
          <Spinner loading={pendingRequests > 0} />
        </div>
      )}

      <Modal
        form
        disableBackdropClick
        disableEscapeKeyDown
        ref={modalRef}
        title={t('s.cadastro.modeloveiculo.operacoes')}
        maxWidth="lg"
        component={(props) => (
          <TransferComponent
            {...props}
            id={initials.idmodeloveiculo}
            right={operations}
            left={disabled}
            insertData={(values) => setOperations(values)}
            removeData={(values) => setDisabled(values)}
          />
        )}
      />
    </>
  );
}

CreateOrUpdate.propTypes = {
  reference: PropTypes.objectOf(PropTypes.any).isRequired,
  create: PropTypes.bool.isRequired,
  data: PropTypes.objectOf(PropTypes.any),
  toggleLoading: PropTypes.func,
  toggleConfirmButton: PropTypes.func,
};

CreateOrUpdate.defaultProps = {
  data: {},
  toggleLoading: () => {},
  toggleConfirmButton: () => {},
};
